import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Truck, RefreshCw, ChevronRight, CheckCircle2, QrCode, X, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return "—"; } };

/**
 * Jun-2026 (User-Wunsch) — Offene Auslieferung: alle als Auslieferung (intern)
 * markierten Werkstattaufträge, die NOCH NICHT als erledigt bestätigt und NICHT
 * abgerechnet sind. Zeigt Pflichtdaten + QR-Code zur Mobil-Erfassung.
 */
export default function OpenDeliveryOrdersTab() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [qrOrder, setQrOrder] = useState(null);
    const [qrUrl, setQrUrl] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/workshop/delivery-orders/open");
            setOrders(r.data?.orders || []);
        } catch {
            toast.error("Offene Auslieferung konnte nicht geladen werden");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openQr = async (o) => {
        setQrOrder(o);
        setQrUrl(null);
        try {
            const res = await api.get(`/work-orders/${o.id}/capture-qr.png`, { responseType: "blob" });
            setQrUrl(URL.createObjectURL(res.data));
        } catch {
            toast.error("QR-Code konnte nicht geladen werden");
        }
    };
    const closeQr = () => {
        if (qrUrl) URL.revokeObjectURL(qrUrl);
        setQrUrl(null);
        setQrOrder(null);
    };

    return (
        <div data-testid="wo-delivery-open">
            <div className="flex items-center gap-2 mb-3">
                <Truck className="w-4 h-4 text-amber-600" />
                <h2 className="font-display font-black text-lg">Offene Auslieferung</h2>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#111827] text-[#FFCC00] px-2 py-0.5 rounded-sm font-mono-num">{orders.length}</span>
                <button onClick={load} className="ml-auto text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827] inline-flex items-center gap-1" data-testid="wo-delivery-open-refresh">
                    <RefreshCw className="w-3 h-3" /> Aktualisieren
                </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Alle Auslieferungs-Aufträge, die noch <strong>nicht als erledigt bestätigt</strong> und <strong>nicht abgerechnet</strong> sind. Über <strong>QR</strong> öffnet sich die Mobil-Erfassung für Teile & Arbeitszeiten.</p>

            {loading ? (
                <div className="flex items-center gap-2 text-gray-500 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Lade Auslieferungs-Aufträge…</div>
            ) : orders.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-gray-500" data-testid="wo-delivery-open-empty">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    Keine offene Auslieferung — alle erledigt oder abgerechnet.
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 text-left text-[10px] uppercase tracking-wider text-gray-500">
                                <th className="px-3 py-2">Auftrag</th>
                                <th className="px-3 py-2">Fahrzeug / FIN</th>
                                <th className="px-3 py-2">Verkäufer · Kunde</th>
                                <th className="px-3 py-2">Standort · Schlüssel</th>
                                <th className="px-3 py-2">Auslieferung</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2 text-right">Summe</th>
                                <th className="px-3 py-2 text-right">Aktion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => {
                                const m = o.delivery_meta || {};
                                const cust = [m.customer_first_name, m.customer_last_name].filter(Boolean).join(" ") || "—";
                                return (
                                    <tr key={o.id} onClick={() => navigate(`/dealer/workshop-orders?focus=${o.id}`)} className="border-t border-gray-100 align-top cursor-pointer hover:bg-amber-50/60 transition-colors" title="Auftrag öffnen" data-testid={`wo-delivery-open-row-${o.id}`}>
                                        <td className="px-3 py-2 font-bold font-mono-num">
                                            {o.order_number}
                                            {o.site_label && (
                                                <span className="ml-1.5 inline-block text-[9px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700 rounded-sm px-1.5 py-0.5 font-sans align-middle" data-testid={`wo-delivery-open-site-${o.id}`}>{o.site_label}</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div>{o.vehicle_label}</div>
                                            {m.vin && <div className="text-[11px] font-mono-num text-gray-400">{m.vin}</div>}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="text-gray-700">{m.seller_name || "—"}</div>
                                            <div className="text-[11px] text-gray-400">{cust}</div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="text-gray-700">{m.site_code || "—"}</div>
                                            <div className="text-[11px] text-gray-400">{m.leitzahl || m.schluessel_nummer || "—"}</div>
                                        </td>
                                        <td className="px-3 py-2 text-gray-600">{fmtDate(m.delivery_date)}</td>
                                        <td className="px-3 py-2">
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 rounded-sm px-2 py-0.5">{o.status_label}</span>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono-num">{fmtEur(o.gross_total)}</td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <button type="button" onClick={(e) => { e.stopPropagation(); openQr(o); }}
                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] border border-gray-300 rounded-sm px-2 py-1 bg-white hover:bg-gray-50 mr-1"
                                                data-testid={`wo-delivery-open-qr-${o.id}`}>
                                                <QrCode className="w-3 h-3" /> QR
                                            </button>
                                            {o.listing_id && (
                                                <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/edit-listing/${o.listing_id}?focus=disposition`); }}
                                                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] border border-gray-300 rounded-sm px-2 py-1 bg-white hover:bg-gray-50"
                                                    data-testid={`wo-delivery-open-go-${o.id}`}>
                                                    Disposition <ChevronRight className="w-3 h-3" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* QR-Modal */}
            {qrOrder && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="wo-delivery-qr-modal">
                    <div className="bg-white rounded-sm p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display font-black text-base">Mobil-Erfassung · {qrOrder.order_number}</h3>
                            <button onClick={closeQr} className="text-gray-400 hover:text-gray-700" data-testid="wo-delivery-qr-close"><X className="w-5 h-5" /></button>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">QR mit dem Handy scannen, um <strong>Teile & Arbeitszeiten</strong> zu erfassen.</p>
                        <div className="flex items-center justify-center min-h-[220px]">
                            {qrUrl ? (
                                <img src={qrUrl} alt="QR-Code Mobil-Erfassung" className="w-52 h-52" data-testid="wo-delivery-qr-img" />
                            ) : (
                                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                            )}
                        </div>
                        <button type="button" onClick={() => navigate(`/werkstatt/erfassen/${qrOrder.id}`)}
                            className="mt-4 w-full h-11 rounded-sm bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black uppercase tracking-wider text-sm inline-flex items-center justify-center gap-1.5"
                            data-testid="wo-delivery-qr-open">
                            <Smartphone className="w-4 h-4" /> Erfassung hier öffnen
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
